import React, { useMemo } from 'react';
import { Card, CardContent } from '../../../components/UI/Card';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import SectionHeader from '../../../components/UI/SectionHeader';
import type { AreaCodeGapRow } from '../../../services/phoneNumberHealthService';
import { sortTimezones } from './healthDashboardUtils';
import InventoryGapsPanel from './InventoryGapsPanel';

const TZ_COLORS: Record<string, string> = {
  Eastern: '#3b82f6',
  Central: '#8b5cf6',
  Mountain: '#f59e0b',
  Pacific: '#10b981',
  Unknown: '#6b7280',
  Other: '#94a3b8',
};

interface Props {
  data: {
    timezones: Array<{
      timezone: string;
      total: number;
      healthy: number;
      warning: number;
      replace: number;
      warmUp: number;
      uniqueAreaCodes: number;
    }>;
    chart: Array<{ name: string; value: number; percent: number }>;
  } | null;
  gaps?: AreaCodeGapRow[];
  periodLabel?: string;
  onPurchaseArea?: (areaCode: string) => void;
}

function healthBarSegments(tz: { healthy: number; warning: number; replace: number; warmUp: number; total: number }) {
  if (tz.total <= 0) return [];
  return [
    { count: tz.healthy, color: 'bg-green-500' },
    { count: tz.warning, color: 'bg-amber-500' },
    { count: tz.replace, color: 'bg-red-500' },
    { count: tz.warmUp, color: 'bg-blue-500' },
  ].filter((s) => s.count > 0);
}

const TimezoneInventory: React.FC<Props> = ({ data, gaps = [], periodLabel = 'Today', onPurchaseArea }) => {
  const scrollToGapsTable = () => {
    document.getElementById('area-code-gaps-table')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (!data || data.timezones.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        No timezone inventory data yet. Run migration and backfill to populate area codes.
      </div>
    );
  }

  const timezones = sortTimezones(data.timezones);
  const unknownTz = timezones.find((t) => t.timezone === 'Unknown');

  const chartData = data.chart.map((c) => ({
    name: c.name,
    value: c.value,
    percent: c.percent,
    color: TZ_COLORS[c.name] ?? '#6b7280',
  }));

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Inventory by Timezone"
        description="Number distribution and health breakdown across US time zones."
      />

      {unknownTz && unknownTz.total > 0 && (
        <div className="rounded-lg border border-amber-300/40 bg-amber-50/50 dark:bg-amber-950/20 px-4 py-2.5 text-sm text-amber-900 dark:text-amber-100">
          <span className="font-medium">{unknownTz.total} numbers</span> have unknown timezone — area codes need enrichment for accurate routing.
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4 items-stretch">
        <div className="xl:col-span-3 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-3">
          {timezones.map((tz) => {
            const accent = TZ_COLORS[tz.timezone] ?? '#6b7280';
            const segments = healthBarSegments(tz);
            return (
              <Card
                key={tz.timezone}
                className="border-border/80 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                style={{ borderLeftWidth: 3, borderLeftColor: accent }}
              >
                <CardContent className="p-3.5">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: accent }} />
                      <h3 className="text-sm font-semibold truncate">{tz.timezone}</h3>
                    </div>
                    <span className="text-lg font-bold tabular-nums">{tz.total}</span>
                  </div>

                  {segments.length > 0 && (
                    <div className="flex h-1.5 rounded-full overflow-hidden bg-muted/40 mb-2.5">
                      {segments.map((s, i) => (
                        <div key={i} className={s.color} style={{ width: `${(s.count / tz.total) * 100}%` }} />
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <div className="flex justify-between text-green-600 dark:text-green-400">
                      <span>Healthy</span><span className="font-medium tabular-nums">{tz.healthy}</span>
                    </div>
                    <div className="flex justify-between text-amber-600 dark:text-amber-400">
                      <span>Warning</span><span className="font-medium tabular-nums">{tz.warning}</span>
                    </div>
                    <div className="flex justify-between text-red-600 dark:text-red-400">
                      <span>Replace</span><span className="font-medium tabular-nums">{tz.replace}</span>
                    </div>
                    <div className="flex justify-between text-blue-600 dark:text-blue-400">
                      <span>Warm-Up</span><span className="font-medium tabular-nums">{tz.warmUp}</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-2 pt-2 border-t border-border/50">
                    {tz.uniqueAreaCodes} unique area codes
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <Card className="xl:col-span-5 border-border/80 shadow-sm">
          <CardContent className="p-4 h-full flex flex-col min-h-[22rem]">
            <p className="text-sm font-semibold mb-0.5">Distribution</p>
            <p className="text-xs text-muted-foreground mb-4">Share of active numbers per timezone</p>
            <div className="flex-1 min-h-[16rem]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="46%"
                    innerRadius={58}
                    outerRadius={96}
                    paddingAngle={chartData.length > 1 ? 3 : 0}
                    label={({ name, payload }) =>
                      (payload?.percent ?? 0) >= 6 ? `${name} ${(payload?.percent ?? 0).toFixed(0)}%` : ''
                    }
                    labelLine={false}
                  >
                    {chartData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} stroke="transparent" />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid var(--border)',
                      background: 'hsl(var(--card))',
                      fontSize: 12,
                    }}
                    formatter={(value: number, _name, props: { payload?: { name?: string; percent?: number } }) => [
                      `${value} numbers (${(props.payload?.percent ?? 0).toFixed(1)}%)`,
                      props.payload?.name ?? '',
                    ]}
                  />
                  <Legend verticalAlign="bottom" height={40} iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <div className="xl:col-span-4">
          <InventoryGapsPanel
            gaps={gaps}
            periodLabel={periodLabel}
            onPurchaseArea={onPurchaseArea}
            onViewAll={scrollToGapsTable}
          />
        </div>
      </div>
    </div>
  );
};

export default TimezoneInventory;
